import React from "react";
import { motion } from "motion/react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

export type BadgeVariant =
  | "success"
  | "error"
  | "warning"
  | "processing"
  | "neutral"
  | "everpine";
type PulseVariant = BadgeVariant;
type BadgeProps = {
  message: string | React.ReactNode;
  variant?: BadgeVariant;
  pulseVariant?: PulseVariant;
  usePulseIndicator?: boolean;
  customClasses?: Record<string, boolean>;
};

export const Badge: React.FC<BadgeProps> = ({
  message,
  variant = "processing",
  pulseVariant,
  usePulseIndicator = false,
  customClasses,
}) => {
  const currentPulseVariant = pulseVariant || variant;
  const pulseColorClasses: Record<PulseVariant, string> = {
    success: "bg-brand-sprig-900",
    error: "bg-error-600",
    warning: "bg-warning-600",
    processing: "bg-brand-everpine-900",
    neutral: "bg-neutral-black",
    everpine: "bg-brand-everpine-900",
  };
  const pulseClass = clsx(
    "w-2 h-2 rounded-full",
    pulseColorClasses[currentPulseVariant]
  );

  const colorClasses: Record<BadgeVariant, string> = {
    success: "bg-brand-sprig-900 text-neutral-white",
    error: "bg-error-100 text-error-600",
    warning: "bg-warning-50 text-warning-600",
    processing: "bg-[#DEE7E4] text-brand-everpine-900",
    neutral: "bg-neutral-100 text-neutral-black",
    everpine: "bg-brand-tarnish-100 text-brand-everpine-900",
  };
  const wrapperClass = twMerge(
    clsx(
      "flex items-center px-2 gap-1 min-h-[20px] rounded",
      "text-xs font-semibold",
      colorClasses[variant],
      customClasses
    )
  );

  return (
    <div className="flex items-center gap-1">
      {usePulseIndicator && (
        <motion.div
          className={pulseClass}
          animate={{ scale: [1, 0.8, 1], opacity: [1, 0.4, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      <div className={wrapperClass}>
        {typeof message === "string" ? <span>{message}</span> : message}
      </div>
    </div>
  );
};
